import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../../store/authStore'
import { supabase } from '../../../lib/supabase'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

export const useExternalCalendars = () => {
  const household = useAuthStore((s) => s.household)
  const qc        = useQueryClient()
  const key       = ['external-calendars', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('external_calendars')
        .select('id, name, ics_url, color, last_synced, event_count')
        .eq('household_id', household.id)
        .order('created_at')
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const addCalendar = useMutation({
    mutationFn: async ({ name, ics_url, color }) => {
      const { data: { session } } = await supabase.auth.getSession()
      const { data, error } = await supabase
        .from('external_calendars')
        .insert({ household_id: household.id, name, ics_url, color: color ?? '#64748b', created_by: session.user.id })
        .select('id')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: inv,
  })

  const deleteCalendar = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('external_calendars').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: inv,
  })

  const syncCalendar = useMutation({
    mutationFn: async (external_cal_id) => {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/sync-ical`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey':        SUPABASE_KEY,
        },
        body: JSON.stringify({ external_cal_id }),
      })
      const body = await res.json()
      if (!res.ok) throw new Error(body.error ?? 'Sync failed')
      return body
    },
    onSuccess: (_, id) => {
      inv()
      qc.invalidateQueries({ queryKey: ['external-events'] })
    },
  })

  return { ...query, addCalendar, deleteCalendar, syncCalendar }
}

// ── External events for calendar range ───────────────────────────────────────
export const useExternalEvents = (rangeStart, rangeEnd, hiddenIds) => {
  const household = useAuthStore((s) => s.household)

  return useQuery({
    queryKey: ['external-events', household?.id, rangeStart, rangeEnd],
    enabled:  !!household?.id,
    staleTime: 0,
    refetchInterval: 5 * 60_000,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('external_events')
        .select(`id, uid, title, start_time, start_date, end_time, end_date,
          is_all_day, description, location, recurrence_rule, external_cal_id,
          external_calendars(id, name, color)`)
        .eq('household_id', household.id)
        .or(
          `start_time.gte.${rangeStart},start_date.gte.${rangeStart.slice(0, 10)}`
        )
        .or(
          `start_time.lte.${rangeEnd},start_date.lte.${rangeEnd.slice(0, 10)}`
        )
      if (error) throw error
      return (data ?? [])
        .filter((e) => !hiddenIds.has(e.external_cal_id))
        .map((e) => ({
          ...e,
          _external: true,
          _key: `ext:${e.id}`,
          // Normalise to the shapes CalendarPage/MonthGrid/WeekView/DayView expect
          start_time:  e.is_all_day ? (e.start_date ?? null) : e.start_time,
          end_time:    e.is_all_day ? (e.end_date   ?? null) : e.end_time,
          calendar_id: e.external_cal_id,
          calendars:   e.external_calendars,
          event_members: [],
        }))
    },
  })
}
